import { Component, effect, inject, input, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormDatePickerComponent } from '@shared/forms';
import { UnavailableComponent } from '@shared/components';
import { LicenseFacade } from '@app/store/license';
import { Time } from '@app/shared/classes';
import { minEndAtValidator } from './min-end-at.validator';

const MIN_DAYS_AHEAD = 2;

@Component({
    selector: 'app-license-edit-modal',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.css'],
    imports: [DatePipe, ReactiveFormsModule, TranslateModule, ButtonModule, FormDatePickerComponent, UnavailableComponent],
})
export class EditComponent implements OnInit, OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly licenseFacade = inject(LicenseFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);

    readonly id = input.required<string>();

    readonly license = this.licenseFacade.license;
    readonly loading = this.licenseFacade.loading;
    readonly saving = this.licenseFacade.saving;
    readonly error = this.licenseFacade.error;
    readonly minEndAt = this.buildMinEndAt();

    readonly form = this.formBuilder.group({
        endAt: this.formBuilder.control<Date | null>(null, [FormValidators.Required, minEndAtValidator(this.minEndAt, MIN_DAYS_AHEAD)]),
    });

    constructor() {
        effect(() => {
            const license = this.license();
            if (license) {
                this.form.patchValue({ endAt: license.endAt });
            }
        });

        effect(() => {
            if (this.licenseFacade.saved()) {
                this.close();
            }
        });
    }

    ngOnInit() {
        this.licenseFacade.getLicense(this.id());
    }

    ngOnDestroy(): void {
        this.licenseFacade.clear();
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        const value = this.form.getRawValue();

        if (this.form.valid && value.endAt) {
            this.licenseFacade.updateLicense(this.id(), { endAt: Time.dateToString(value.endAt) });
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }], { relativeTo: this.route.parent?.parent });
    }

    private buildMinEndAt(): Date {
        const date = new Date();
        date.setDate(date.getDate() + MIN_DAYS_AHEAD);
        date.setHours(0, 0, 0, 0);
        return date;
    }
}
